import { useState, useEffect, useCallback } from 'react';
import axios from '../lib/axios';

// API base URL with /api prefix
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert VAPID key from URL-safe base64 to Uint8Array
// Edge is very strict about this conversion
function urlBase64ToUint8Array(base64String) {
  // Remove any whitespace
  const cleanedString = base64String.trim();
  
  // Calculate padding needed
  const paddingLength = (4 - (cleanedString.length % 4)) % 4;
  const padding = '='.repeat(paddingLength);
  
  // Convert URL-safe base64 to standard base64
  const base64 = (cleanedString + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  // Debug logging
  console.log('[Push] VAPID key conversion:', {
    original: cleanedString.substring(0, 20) + '...',
    originalLength: cleanedString.length,
    paddingAdded: paddingLength,
    convertedLength: base64.length
  });
  
  try {
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    
    console.log('[Push] Decoded key length:', outputArray.length, 'bytes (expected: 65)');
    
    // Validate: VAPID public key should be 65 bytes (uncompressed EC point)
    if (outputArray.length !== 65) {
      console.error('[Push] Invalid VAPID key length! Expected 65, got', outputArray.length);
    }
    
    // Validate: First byte should be 0x04 (uncompressed point indicator)
    if (outputArray[0] !== 0x04) {
      console.error('[Push] Invalid VAPID key format! First byte should be 0x04, got', outputArray[0]);
    }
    
    return outputArray;
  } catch (e) {
    console.error('[Push] Failed to decode VAPID key:', e);
    throw new Error('Invalid VAPID key format');
  }
}

export function usePushNotifications(user) {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [iosInfo, setIosInfo] = useState({ isIOS: false, isPWA: false, needsPWA: false });

  // Check if push notifications are supported
  useEffect(() => {
    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // Check if running as installed PWA (standalone mode)
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  window.navigator.standalone === true;
    
    // Basic support check
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    const hasNotification = 'Notification' in window;
    
    // Debug logging
    console.log('[Push] Environment check:', {
      isIOS,
      isPWA,
      hasServiceWorker,
      hasPushManager,
      hasNotification,
      notificationPermission: hasNotification ? Notification.permission : 'N/A'
    });
    
    // For iOS, push is only supported in PWA mode (iOS 16.4+)
    let supported = hasServiceWorker && hasPushManager && hasNotification;
    
    if (isIOS) {
      // On iOS, push only works in installed PWA
      supported = supported && isPWA;
      setIosInfo({ 
        isIOS: true, 
        isPWA, 
        needsPWA: !isPWA && hasServiceWorker 
      });
      console.log('[Push] iOS detected - PWA mode:', isPWA, '- Push supported:', supported);
    } else {
      setIosInfo({ isIOS: false, isPWA: false, needsPWA: false });
      console.log('[Push] Non-iOS platform - Push supported:', supported);
    }
    
    setIsSupported(supported);
    
    if (supported && hasNotification) {
      setPermission(Notification.permission);
    }
  }, []);

  // Register service worker, check existing subscription, and verify with server
  useEffect(() => {
    if (!isSupported || !user) return;

    const checkAndVerifySubscription = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        const existingSub = await registration.pushManager.getSubscription();
        
        if (existingSub) {
          // We have a local subscription - verify it exists on server
          try {
            const subscriptionJson = existingSub.toJSON();
            const verifyResponse = await axios.post(`${API}/push/verify`, {
              endpoint: subscriptionJson.endpoint,
              keys: subscriptionJson.keys
            });
            
            console.log('[Push] Subscription verification:', verifyResponse.data.status);
            
            if (verifyResponse.data.status === 'registered') {
              console.log('[Push] Subscription was re-registered on server');
            }
            
            setSubscription(existingSub);
          } catch (verifyErr) {
            console.error('[Push] Failed to verify subscription:', verifyErr);
            // If verification fails (e.g., not logged in), still set the local subscription
            setSubscription(existingSub);
          }
        } else {
          setSubscription(null);
        }
      } catch (err) {
        console.error('[Push] Error checking push subscription:', err);
      }
    };

    checkAndVerifySubscription();
  }, [isSupported, user]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !user) {
      setError('Push notifications not supported or not logged in');
      return false;
    }

    setIsLoading(true);
    setError(null);

    // Helper function to wait for service worker to be active
    const waitForServiceWorkerActive = async (registration, maxWait = 5000) => {
      if (registration.active) {
        return registration.active;
      }
      
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Service worker activation timeout'));
        }, maxWait);

        const checkState = (sw) => {
          if (sw.state === 'activated' || sw.state === 'active') {
            clearTimeout(timeout);
            resolve(sw);
          }
        };

        // Check installing worker
        if (registration.installing) {
          registration.installing.addEventListener('statechange', function() {
            checkState(this);
          });
        }
        
        // Check waiting worker
        if (registration.waiting) {
          registration.waiting.addEventListener('statechange', function() {
            checkState(this);
          });
        }

        // Also check periodically
        const interval = setInterval(() => {
          if (registration.active) {
            clearInterval(interval);
            clearTimeout(timeout);
            resolve(registration.active);
          }
        }, 100);
      });
    };

    try {
      // Request notification permission
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== 'granted') {
        setError('Notification permission denied');
        setIsLoading(false);
        return false;
      }

      // Get VAPID public key from backend
      const keyResponse = await axios.get(`${API}/push/vapid-key`);
      const vapidPublicKey = keyResponse.data.publicKey;
      console.log('[Push] Got VAPID key from server');

      // Register service worker
      let registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      console.log('[Push] Service worker ready');

      // Try to subscribe to push
      let pushSubscription;
      try {
        pushSubscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });
        console.log('[Push] Subscription created successfully');
      } catch (subscribeError) {
        console.warn('[Push] Initial subscribe failed, attempting recovery:', subscribeError.message);
        
        // If subscription fails (e.g., VAPID key mismatch), try to recover
        // First, unsubscribe any existing subscription
        const existingSub = await registration.pushManager.getSubscription();
        if (existingSub) {
          console.log('[Push] Unsubscribing existing subscription');
          await existingSub.unsubscribe();
        }
        
        // Unregister the service worker to clear cached VAPID key
        console.log('[Push] Unregistering service worker');
        await registration.unregister();
        
        // Wait for cleanup
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Re-register service worker
        console.log('[Push] Re-registering service worker');
        registration = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
        
        // Wait for service worker to be fully active
        console.log('[Push] Waiting for service worker to activate...');
        await waitForServiceWorkerActive(registration);
        await navigator.serviceWorker.ready;
        console.log('[Push] Service worker is now active');
        
        // Try subscribing again with fresh service worker
        console.log('[Push] Retrying subscription with fresh service worker');
        pushSubscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });
        console.log('[Push] Subscription successful after recovery');
      }

      // Send subscription to backend
      const subscriptionJson = pushSubscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      });

      console.log('[Push] Successfully subscribed and registered with server');
      setSubscription(pushSubscription);
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('[Push] Error subscribing to push:', err);
      setError(err.message || 'Failed to subscribe');
      setIsLoading(false);
      return false;
    }
  }, [isSupported, user]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription) return false;

    setIsLoading(true);
    setError(null);

    try {
      // Unsubscribe from push manager
      await subscription.unsubscribe();

      // Remove from backend
      await axios.delete(`${API}/push/unsubscribe`, {
        params: { endpoint: subscription.endpoint }
      });

      console.log('[Push] Successfully unsubscribed');
      setSubscription(null);
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('[Push] Error unsubscribing from push:', err);
      setError(err.message || 'Failed to unsubscribe');
      setIsLoading(false);
      return false;
    }
  }, [subscription]);

  // Update app badge count
  const updateBadge = useCallback(async (count) => {
    try {
      if ('setAppBadge' in navigator) {
        if (count > 0) {
          await navigator.setAppBadge(count);
        } else {
          await navigator.clearAppBadge();
        }
      }
      
      // Also notify service worker
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        if (registration.active) {
          registration.active.postMessage({
            type: count > 0 ? 'SET_BADGE_COUNT' : 'CLEAR_BADGE',
            count
          });
        }
      }
    } catch (err) {
      console.log('[Push] Badge update not supported:', err);
    }
  }, []);

  return {
    permission,
    subscription,
    isSupported,
    isLoading,
    error,
    isSubscribed: !!subscription,
    iosInfo,
    subscribe,
    unsubscribe,
    updateBadge
  };
}

export default usePushNotifications;
